import * as UI from "@iyk/music-ui"
import { AppLink, type AppRoute } from "@iyk/react-router/client"
import type React from "react"
import { Link, useLocation } from "react-router"
import { AppRoutes } from "../../../AppRoutes"
import { Logo } from "../Logo"
import { ShellHeader } from "./ShellHeader"
import { ShellMenu } from "./ShellMenu"
import { ShellRoot } from "./ShellRoot"

const App = ({ header, headerPosition, children, right, user }: Shell.App.Props) => {
  const location = useLocation()

  return (
    <ShellRoot.Root>
      <ShellHeader.Root position={headerPosition}>
        <ShellHeader.Part position="left">
          <div className="hidden lg:block">
            <Logo interactive />
          </div>
          <ShellMenu.Trigger className="lg:hidden" />
        </ShellHeader.Part>

        <ShellHeader.Part position="center">{header}</ShellHeader.Part>
        <ShellHeader.Part position="right">
          {user ? (
            <UI.UserMenu
              name={user.name}
              email={user.email}
              avatar={user.avatarUrl}
              logoutRoute={AppRoutes.logout}
              settingsRoute={AppRoutes.settings}
              trigger={<UI.UserAvatar url={user.avatarUrl} name={user.name} size="md" />}
              triggerClassName="rounded-full"
            />
          ) : (
            <Link
              className={UI.classForButton({ kind: "shaded", size: "sm" })}
              to={AppRoutes.login(location.pathname)}
            >
              Login
            </Link>
          )}
        </ShellHeader.Part>
      </ShellHeader.Root>

      <ShellRoot.Left>
        <ShellMenu.Root isSignedIn={Boolean(user)} />
      </ShellRoot.Left>
      <ShellRoot.Content>{children}</ShellRoot.Content>
      <ShellRoot.Right>{right}</ShellRoot.Right>
    </ShellRoot.Root>
  )
}

const Guest = ({ children }: Shell.Guest.Props) => (
  <ShellRoot.Root>
    <ShellHeader.Root position="sticky">
      <ShellHeader.Part position="left">
        <Logo interactive />
      </ShellHeader.Part>
    </ShellHeader.Root>

    <ShellRoot.Content className="px-4 py-6">{children}</ShellRoot.Content>
  </ShellRoot.Root>
)

const Minimal = ({ children }: Shell.Minimal.Props) => (
  <ShellRoot.Root hasHeader={false}>
    <ShellRoot.Content>{children}</ShellRoot.Content>
  </ShellRoot.Root>
)

const Title = ({ title, font, route }: Shell.Title.Props) => {
  return (
    <AppLink to={route} className={UI.cx(font === "departure" ? "text-xs-departure" : "text-sm")}>
      {title}
    </AppLink>
  )
}

export namespace Shell {
  export namespace App {
    export type User = {
      id: string
      name: string
      email: string
      avatarUrl?: string
    }

    export type Props = {
      children: React.ReactNode
      headerPosition: ShellHeader.Root.Position
      header?: React.ReactNode
      right?: React.ReactNode
      user?: Shell.App.User
    }
  }

  export namespace Guest {
    export type Props = {
      children: React.ReactNode
    }
  }

  export namespace Minimal {
    export type Props = {
      children: React.ReactNode
    }
  }

  export namespace Title {
    export type Font = "departure" | "regola"

    export type Props = {
      title: string
      font: Font
      route: AppRoute
    }
  }
}

export const Shell = {
  App,
  Guest,
  Title,
  Minimal
}
